const channel = "nkriz-soundcloud-helper-v1";
export function allowedSoundCloudUrl(value: string): boolean {
  try { const url = new URL(value); return url.protocol === "https:" && (url.hostname === "soundcloud.com" || url.hostname.endsWith(".soundcloud.com") || url.hostname.endsWith(".sndcdn.com") || url.hostname === "playback.media-streaming.soundcloud.cloud"); }
  catch { return false; }
}

type HelperResponse = { status: number; type: string; body: string; version?: string; transport?: string };
const statusListeners = new Set<(message: string) => void>();
export function onSoundCloudNetworkStatus(listener: (message: string) => void) { statusListeners.add(listener); return () => { statusListeners.delete(listener); }; }
function report(message: string) { for (const listener of statusListeners) listener(message); }
let helperAvailable = false;
let helperCheckedAt = 0;
export function resetSoundCloudConnection() { helperAvailable = false; helperCheckedAt = 0; }
function helperRequest(url?: string): Promise<HelperResponse> {
  return new Promise((resolve, reject) => {
    const id = crypto.randomUUID();
    const timer = window.setTimeout(() => { window.removeEventListener("message", listener); reject(new Error(url ? `Chrome helper timed out reaching ${new URL(url).hostname}. Reload the helper in chrome://extensions and retry.` : "SoundCloud browser helper did not respond")); }, url ? 65_000 : 3_000);
    function listener(event: MessageEvent) {
      if (event.source !== window || event.data?.channel !== channel || event.data?.id !== id) return;
      if (event.data.direction === "progress") { report(String(event.data.message)); return; }
      if (event.data.direction !== "response") return;
      clearTimeout(timer);
      window.removeEventListener("message", listener);
      if (event.data.error) reject(new Error(String(event.data.error)));
      else if (!event.data.result || typeof event.data.result.status !== "number" || typeof event.data.result.body !== "string") reject(new Error("Chrome helper sent an incomplete response. Reload it in chrome://extensions."));
      else resolve(event.data.result);
    }
    window.addEventListener("message", listener);
    window.postMessage({ channel, direction: "request", id, url }, window.location.origin);
  });
}

export async function soundcloudFetch(url: string): Promise<Response> {
  if (!allowedSoundCloudUrl(url)) { let host = "invalid URL"; try { host = new URL(url).hostname; } catch { /* keep validation message */ } throw new Error(`Refused a URL outside SoundCloud’s media hosts: ${host}`); }
  if (!helperAvailable && Date.now() - helperCheckedAt > 2_000) {
    report("Looking for the Chrome helper…");
    try { const result = await helperRequest(); helperAvailable = true; report(`Chrome helper ${result.version || "1.0.0"} connected. Contacting SoundCloud…`); }
    catch { helperAvailable = false; report("Helper not detected; trying this page’s connection…"); }
    helperCheckedAt = Date.now();
  }
  if (helperAvailable) {
    let response: HelperResponse;
    try { response = await helperRequest(url); }
    catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (/failed to fetch|networkerror/i.test(message)) throw new Error(`Chrome helper could not reach ${new URL(url).hostname}. Reload the updated NKriZ helper in chrome://extensions, reload this player, and keep a SoundCloud tab open with VeePN connected.`);
      throw error;
    }
    const binary = atob(response.body);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new Response(bytes, { status: response.status, headers: { "Content-Type": response.type } });
  }
  report(`Connecting to ${new URL(url).hostname} from this page…`);
  try { return await fetch(url, { credentials: "omit", signal: AbortSignal.timeout(15_000), referrerPolicy: "no-referrer" }); }
  catch { throw new Error("The player could not detect a working Chrome helper. Reload NKriZ SoundCloud browser helper in chrome://extensions, then reload this player and retry with VeePN connected."); }
}

export async function soundcloudJson<T>(url: string): Promise<T> {
  const response = await soundcloudFetch(url);
  if (!response.ok) throw new Error(`SoundCloud returned HTTP ${response.status}`);
  return response.json() as Promise<T>;
}
